import * as React from "react";
import { useState, useEffect } from "react";
import ProjectService from "src/app/project/ProjectService";

interface SmsPurchasePopupProps {
  show: boolean;
  onClose: () => void;
  corporates: Array<{
    id: string;
    name: string;
  }>;
  selectedCorporate: {
    id: string;
    name: string;
  } | null;
  onCorporateSelect: (corp: { id: string; name: string } | null) => void;
  onSmsAdded: () => void;
}

const SmsPurchasePopup = ({
  show,
  onClose,
  corporates,
  selectedCorporate,
  onCorporateSelect,
  onSmsAdded,
}: SmsPurchasePopupProps) => {
  const [smsCount, setSmsCount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [paymentDate, setPaymentDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [pricePerSms, setPricePerSms] = useState(0.5);
  const [totalValuation, setTotalValuation] = useState(0);

  useEffect(() => {
    setTotalValuation(smsCount * pricePerSms);
  }, [smsCount, pricePerSms]);

  const todayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const resetForm = () => {
    setSmsCount(0);
    setPaymentStatus("unpaid");
    setPaymentDate("");
    setReferenceNumber("");
    setRemarks("");
    setPricePerSms(0.5);
  };

  const handleAddSmsCredits = async () => {
    if (!selectedCorporate || !selectedCorporate.id) {
      alert("Please select a corporate");
      return;
    }

    if (!smsCount || smsCount <= 0) {
      alert("Please enter a valid number of SMS credits");
      return;
    }

    const isPaid = paymentStatus === "paid";

    try {
      await ProjectService.purchaseSmsCredits(selectedCorporate.id, {
        count: smsCount,
        isPaid: isPaid,
        remarks: remarks,
        pricePerSms: pricePerSms,
        ...(isPaid && {
          paymentDate: paymentDate,
          referenceNumber: referenceNumber,
        }),
      });

      alert("SMS credits added successfully!");
      resetForm();
      onSmsAdded();
      onClose();
    } catch (error) {
      console.error("Error adding SMS credits:", error);
      alert("Failed to add SMS credits. Please try again.");
    }
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(50, 64, 84, 0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "32px",
          width: "700px",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Header with title and close button */}
        <div
          style={{
            backgroundColor: "#ffffff",
            boxShadow: "0px 4px 40px 0px rgba(0, 0, 0, 0.1)",
            padding: "20px 24px",
            color: "rgba(60, 66, 87, 1)",
            fontSize: 16,
            fontFamily: "'Poppins', sans-serif",
            height: 69,
            fontWeight: 600,
            margin: "-31px -31px 10px -31px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#324054",
              fontFamily: "'Poppins', sans-serif",
              margin: 0,
            }}
          >
            ADD SMS CREDITS
          </div>
          <button
            onClick={() => {
              onClose();
              resetForm();
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "36px",
              color: "#324054",
              padding: "0",
              display: "flex",
              alignItems: "center",
            }}
          >
            ×
          </button>
        </div>

        <form style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {/* Corporate Field */}
          <div>
            <label
              style={{
                display: "block",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: 500,
                color: "#324054",
                marginBottom: "8px",
              }}
            >
              Corporate <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <select
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #E5E7EB",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: 500,
                outline: "none",
                fontFamily: "'Poppins', sans-serif",
                boxSizing: "border-box",
                color: "#324054",
              }}
              value={selectedCorporate ? selectedCorporate.id : ""}
              onChange={(e) => {
                const corp = corporates.find((c) => c.id === e.target.value);
                onCorporateSelect(corp || null);
              }}
            >
              <option value="">Select Corporate</option>
              {corporates.map((corp) => (
                <option key={corp.id} value={corp.id}>
                  {corp.name}
                </option>
              ))}
            </select>
          </div>

          {/* SMS Count, Price, and Valuation Fields */}
          <div style={{ display: "flex", gap: "16px" }}>
            {/* Number of SMS */}
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#324054",
                  marginBottom: "8px",
                }}
              >
                Number of SMS <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #E5E7EB",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: 500,
                  outline: "none",
                  fontFamily: "'Poppins', sans-serif",
                  boxSizing: "border-box",
                  color: "#324054",
                }}
                type="number"
                min="1"
                max="10000"
                value={smsCount}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 1 && value <= 10000) {
                    setSmsCount(value);
                  }
                }}
              />
            </div>

            {/* Price per SMS */}
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#324054",
                  marginBottom: "8px",
                }}
              >
                Price per SMS (Rs) <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #E5E7EB",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: 500,
                  outline: "none",
                  fontFamily: "'Poppins', sans-serif",
                  boxSizing: "border-box",
                  color: "#324054",
                }}
                type="number"
                step="0.01"
                min="0.01"
                value={pricePerSms}
                onChange={(e) => setPricePerSms(parseFloat(e.target.value))}
              />
            </div>

            {/* Total Valuation */}
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#324054",
                  marginBottom: "8px",
                }}
              >
                Total Valuation (Rs)
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #E5E7EB",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: 500,
                  outline: "none",
                  fontFamily: "'Poppins', sans-serif",
                  boxSizing: "border-box",
                  color: "#324054",
                  backgroundColor: "#f9fafb",
                }}
                type="text"
                value={`${totalValuation.toFixed(2)}`}
                readOnly
              />
            </div>
          </div>

          {/* Payment Status */}
          <div>
            <label
              style={{
                display: "block",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: 500,
                color: "#324054",
                marginBottom: "8px",
              }}
            >
              Payment Status <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <div style={{ display: "flex", gap: "16px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                <input
                  type="radio"
                  checked={paymentStatus === "unpaid"}
                  onChange={() => setPaymentStatus("unpaid")}
                  style={{
                    width: "14px",
                    height: "14px",
                    accentColor: "#2563eb",
                  }}
                />
                <span>Unpaid</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                <input
                  type="radio"
                  checked={paymentStatus === "paid"}
                  onChange={() => setPaymentStatus("paid")}
                  style={{
                    width: "14px",
                    height: "14px",
                    accentColor: "#2563eb",
                  }}
                />
                <span>Paid</span>
              </label>
            </div>
          </div>

          {/* Payment Date and Reference Number (conditional) */}
          {paymentStatus === "paid" && (
            <div style={{ display: "flex", gap: "16px" }}>
              {/* Payment Date */}
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#324054",
                    marginBottom: "8px",
                  }}
                >
                  Payment Date <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <input
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: 500,
                    outline: "none",
                    fontFamily: "'Poppins', sans-serif",
                    boxSizing: "border-box",
                    color: "#324054",
                  }}
                  type="date"
                  max={todayDate()}
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </div>

              {/* Reference Number */}
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#324054",
                    marginBottom: "8px",
                  }}
                >
                  Reference Number <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <input
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: 500,
                    outline: "none",
                    fontFamily: "'Poppins', sans-serif",
                    boxSizing: "border-box",
                    color: "#324054",
                  }}
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Remarks */}
          <div>
            <label
              style={{
                display: "block",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: 500,
                color: "#324054",
                marginBottom: "8px",
              }}
            >
              Remarks
            </label>
            <textarea
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #E5E7EB",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: 500,
                outline: "none",
                fontFamily: "'Poppins', sans-serif",
                boxSizing: "border-box",
                color: "#324054",
                minHeight: "80px",
                resize: "vertical",
              }}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "16px",
              paddingTop: "16px",
              position: "sticky",
              bottom: 0,
              backgroundColor: "#FFFFFF",
              paddingBottom: "8px",
            }}
          >
            <button
              type="button"
              style={{
                padding: "8px 16px",
                borderRadius: "4px",
                fontWeight: 500,
                fontSize: "14px",
                cursor: "pointer",
                minWidth: "120px",
                fontFamily: "'Poppins', sans-serif",
                backgroundColor: "#FFFFFF",
                color: "#324054",
                border: "1px solid #324054",
              }}
              onClick={() => {
                onClose();
                resetForm();
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{
                padding: "8px 16px",
                borderRadius: "4px",
                fontWeight: 500,
                fontSize: "14px",
                cursor: "pointer",
                minWidth: "120px",
                fontFamily: "'Poppins', sans-serif",
                backgroundColor: "#2A7CED",
                color: "#FFFFFF",
                border: "none",
              }}
              onClick={handleAddSmsCredits}
              disabled={
                !selectedCorporate ||
                smsCount <= 0 ||
                pricePerSms <= 0 ||
                (paymentStatus === "paid" && (!paymentDate || !referenceNumber))
              }
            >
              Add Credits
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SmsPurchasePopup;